import { _isNil } from '@/littledash';
import { arraysEqual } from '@/utils/array.utils';
import type { Edge } from '@xyflow/react';
import type {
  GraphCanvasConfig,
  GraphCanvasNode,
  GraphCanvasSnapshot,
  GraphChannel,
  GraphSnapshot,
  LoadStatus,
} from './GraphCanvas.model';

/** True when two edge sets have identical connectivity (id + source + target, order-sensitive). */
const sameConnectivity = (a: ReadonlyArray<Edge>, b: ReadonlyArray<Edge>): boolean =>
  arraysEqual(a, b, (x, y) => x.id === y.id && x.source === y.source && x.target === y.target);

/**
 * Identity layout: positions untouched. Used when no layout is configured. Returns fresh node
 * objects (not just a copied array) to honour the `LayoutFn` contract, so external mutation of the
 * input nodes can't leak into the store.
 */
const identityLayout = <TData extends Record<string, unknown>>(
  nodes: ReadonlyArray<GraphCanvasNode<TData>>
): Array<GraphCanvasNode<TData>> => nodes.map((node) => ({ ...node }));

/** No-edges default: a graph with no derived relationships. */
const noEdges = (): Array<Edge> => [];

/**
 * Generic, framework-agnostic reactive store for a React Flow graph. Holds caller-defined node data
 * (`TData`) and metadata (`TMeta`), and derives edges + positions through injected
 * `deriveEdges`/`layout` hooks — so it knows nothing about the graph's domain or data source.
 *
 * Mutations are independent, not a required sequence: `setGraph` replaces the whole graph,
 * `upsertNodes` adds nodes incrementally (id-deduped), `updateMeta` merges metadata, `setLoadError`
 * flags a failed load. A caller can use any subset — a one-shot static graph (`setGraph` alone), an
 * incrementally-built one (`upsertNodes`), or a snapshot-then-live-updates mix. The store is agnostic
 * to whether updates arrive from a fetch, a stream, polling, or static data.
 *
 * Modelled on the mature `StepFormEngine` pattern: private fields, a single `EventTarget`, and
 * getters that return the stored value BY REFERENCE — so the `useSyncExternalStore` snapshot stays
 * referentially stable between mutations and never loops — with `#notify` dispatching a plain
 * `Event(channel)` per channel so consumers re-render only for the slice they subscribe to.
 *
 * Nodes and edges are exposed as one `graph` slice (`getGraph`): edges are a pure projection of nodes,
 * so a mutation always changes both together. Bundling them means one notification and one consumer
 * render per mutation (not two), and the `#graph` container is rebuilt ONLY when nodes or edges
 * actually change — a no-op `upsertNodes` keeps the same reference, so subscribers don't re-render.
 *
 * Within a rebuilt `#graph`, the edges array reference is preserved whenever connectivity is unchanged
 * (`sameConnectivity`), on both mutation paths. The consumer's snapshot is still a fresh object (so the
 * `useSyncExternalStore` hook re-renders), but the stable edges reference lets React Flow's `edges` prop
 * skip internal edge reprocessing when only node data (e.g. a status badge) changed.
 */
export class GraphCanvasEngine<TData extends Record<string, unknown>, TMeta extends Record<string, unknown>> {
  readonly #eventTarget = new EventTarget();
  readonly #nodeIds = new Set<string>();
  readonly #deriveEdges: (nodes: ReadonlyArray<GraphCanvasNode<TData>>) => Array<Edge>;
  readonly #layout: (
    nodes: ReadonlyArray<GraphCanvasNode<TData>>,
    edges: ReadonlyArray<Edge>
  ) => Array<GraphCanvasNode<TData>>;
  #graph: GraphSnapshot<TData> = { nodes: [], edges: [] };
  #meta: TMeta | null = null;
  #loadStatus: LoadStatus = 'loading';

  constructor(config: GraphCanvasConfig<TData> = {}) {
    this.#deriveEdges = config.deriveEdges ?? noEdges;
    this.#layout = config.layout ?? identityLayout;
  }

  /** The reactive nodes+edges slice, reference-stable between mutations. Subscribe via the `graph` channel. */
  getGraph(): GraphSnapshot<TData> {
    return this.#graph;
  }

  /** The bare node array, for imperative point-reads (e.g. reconcile). Reactive consumers use `getGraph`. */
  getNodes(): ReadonlyArray<GraphCanvasNode<TData>> {
    return this.#graph.nodes;
  }

  getMeta(): Readonly<TMeta> | null {
    return this.#meta;
  }

  /**
   * Snapshot-hydration lifecycle: `loading` → `ready` (set by `setGraph`) or `error` (`setLoadError`).
   *
   * IMPORTANT: only `setGraph` flips this to `ready` — `upsertNodes` does NOT. So a consumer that
   * builds a graph purely incrementally (never calling `setGraph`) stays `loading` forever. This is
   * intentional: `loadStatus` tracks *initial hydration*, not "have any nodes arrived". Incremental-only
   * consumers should either call `setGraph({ nodes: [], meta })` once to signal ready, or simply not
   * gate their first paint on `loadStatus`.
   *
   * (Future option, when a real incremental-only consumer exists: add an explicit `markReady()` method
   * so readiness can be signalled without a placeholder empty `setGraph`. Deferred as YAGNI for now.)
   */
  getLoadStatus(): LoadStatus {
    return this.#loadStatus;
  }

  /**
   * Commits a new node set as the `#graph`, re-deriving edges but reusing the prior edges array when
   * connectivity is unchanged — so a change that only touched node data (e.g. a status-badge settle)
   * lets React Flow's `edges` prop skip reprocessing.
   */
  #commitNodes(nodes: Array<GraphCanvasNode<TData>>): void {
    const edges = this.#deriveEdges(nodes);
    const laidOut = this.#layout(nodes, edges);
    this.#graph = { nodes: laidOut, edges: sameConnectivity(edges, this.#graph.edges) ? this.#graph.edges : edges };
  }

  /** One-shot hydration from a snapshot. Derives edges, applies layout, marks load `ready`, notifies. */
  setGraph(snapshot: GraphCanvasSnapshot<TData, TMeta>): void {
    this.#commitNodes(snapshot.nodes);
    this.#meta = snapshot.meta;
    this.#nodeIds.clear();
    this.#graph.nodes.forEach((node) => this.#nodeIds.add(node.id));
    this.#loadStatus = 'ready';
    this.#notify('graph');
    this.#notify('meta');
    this.#notify('load');
  }

  /**
   * Appends nodes incrementally (e.g. from a live stream). Idempotent by node id — nodes already
   * present are skipped, and duplicate ids *within* a single batch collapse to one, so snapshot/stream
   * overlap is a silent no-op and a wholly-duplicate batch notifies nothing. Re-derives edges and
   * re-runs layout over the resulting node set.
   *
   * Cost: each call re-derives edges and re-lays out the ENTIRE node set, so streaming n nodes one at
   * a time is ~O(n²) overall. This is a deliberate simplicity/determinism trade — the target graphs are
   * small (tens of nodes). If a future consumer streams large graphs, make derivation/layout
   * incremental (position only the new node relative to its parent) or coalesce bursts per frame.
   */
  upsertNodes(incoming: ReadonlyArray<GraphCanvasNode<TData>>): void {
    const fresh = incoming.reduce<Array<GraphCanvasNode<TData>>>((acc, node) => {
      if (!this.#nodeIds.has(node.id)) {
        this.#nodeIds.add(node.id);
        acc.push(node);
      }
      return acc;
    }, []);
    if (fresh.length === 0) {
      return; // no new ids — `#graph` reference is untouched, so subscribers don't re-render
    }
    this.#commitNodes([...this.#graph.nodes, ...fresh]);
    this.#notify('graph');
  }

  /**
   * Merges a partial metadata update. No-op before hydration (`#meta` is null until `setGraph`) —
   * callers that may receive a meta update before the snapshot resolves must buffer it and re-apply
   * after `setGraph`. Always notifies when applied (equality is the caller's concern via the partial).
   */
  updateMeta(partial: Partial<TMeta>): void {
    if (_isNil(this.#meta)) {
      return;
    }
    this.#meta = { ...this.#meta, ...partial };
    this.#notify('meta');
  }

  /** Marks hydration as failed so consumers can surface an error instead of an empty canvas. */
  setLoadError(): void {
    if (this.#loadStatus === 'error') {
      return;
    }
    this.#loadStatus = 'error';
    this.#notify('load');
  }

  /** Subscribe to a channel; returns an unsubscribe function. */
  subscribe(channel: GraphChannel, listener: () => void): () => void {
    this.#eventTarget.addEventListener(channel, listener);
    return () => this.#eventTarget.removeEventListener(channel, listener);
  }

  #notify(channel: GraphChannel): void {
    this.#eventTarget.dispatchEvent(new Event(channel));
  }
}
